import { useMemo } from 'react';
import { Button } from '@simplywallst/ui-core/next';
import { getReadableCountry, isMarketSupported } from '@/utilities/markets';
import useUser from '@/hooks/useUser';
import { useUserAgent } from '@simplywallst/hooks/useUserAgent';
import { useLocation } from '@/hooks/useLocation';
import { DEFAULT_INDUSTRY, DEFAULT_SORT_BY } from '@/pages/Stocks/constants';
import { getFlagCharacter } from '@/utilities/getFlagCharacter';
import { getStocksPath } from '@/pages/Stocks/utils';
import type { PrimaryCountryISO } from '@/constants/countries';

const useCountryFlag = (countryCode: PrimaryCountryISO) => {
  const { os } = useUserAgent();
  return os.toLowerCase().includes('windows')
    ? ''
    : getFlagCharacter(countryCode);
};

const useStocksPath = (market: PrimaryCountryISO, isLoggedIn: boolean) => {
  const location = useLocation();
  const { industry = DEFAULT_INDUSTRY, sortBy = DEFAULT_SORT_BY } =
    location.payload;

  return getStocksPath({ market, industry, sortBy, isLoggedIn });
};

const MarketButton = ({
  market,
  isLoggedIn,
}: {
  market: PrimaryCountryISO;
  isLoggedIn: boolean;
}) => {
  const stocksPath = useStocksPath(market, isLoggedIn);
  const countryFlag = useCountryFlag(market);

  const countryDemonym = useMemo(() => {
    if (market === undefined) return;
    if (!isMarketSupported(market)) return;
    return getReadableCountry(market, 'demonym');
  }, [market]);
  if (!countryDemonym) return;

  return (
    <Button href={stocksPath} variant="subtle" size="large" className="h-8">
      {countryFlag && (
        <span className="mr-0.5" aria-hidden="true">
          {countryFlag}{' '}
        </span>
      )}
      Switch to {countryDemonym} market
    </Button>
  );
};

export const SwitchMarketButton = () => {
  const { data: user } = useUser();
  const market = (user.defaultMarketISO || user.countryISO)?.toLowerCase() as
    | PrimaryCountryISO
    | undefined;
  if (!market) return;

  return <MarketButton market={market} isLoggedIn={user.loggedIn} />;
};
